import React, { useState, useEffect } from 'react';
import { cotizacionService, clienteService, servicioService, productoService, uploadService } from '../services/api';
import '../styles/Cotizaciones.css';

const Cotizaciones = () => {
  const [cotizaciones, setCotizaciones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [cotizacionEditando, setCotizacionEditando] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [formData, setFormData] = useState({
    cliente_id: '',
    titulo: '',
    descripcion: '',
    margen_ganancia: 0,
    notas: '',
    fecha_vencimiento: ''
  });
  const [items, setItems] = useState([]);
  const [nuevoItem, setNuevoItem] = useState({
    tipo: 'servicio',
    servicio_id: '',
    producto_id: '',
    descripcion: '',
    cantidad: 1,
    precio_unitario: 0
  });
  const [imagenes, setImagenes] = useState([]);
  const [subiendoImagen, setSubiendoImagen] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, [filtroEstado]);

  const cargarDatos = async () => {
    try {
      const params = filtroEstado ? { estado: filtroEstado } : {};
      const [cotizacionesRes, clientesRes, serviciosRes, productosRes] = await Promise.all([
        cotizacionService.getAll(params),
        clienteService.getAll(),
        servicioService.getAll(),
        productoService.getAll()
      ]);

      setCotizaciones(cotizacionesRes.data);
      setClientes(clientesRes.data);
      setServicios(serviciosRes.data);
      setProductos(productosRes.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirModal = () => {
    setModoEdicion(false);
    setCotizacionEditando(null);
    setFormData({
      cliente_id: '',
      titulo: '',
      descripcion: '',
      margen_ganancia: 0,
      notas: '',
      fecha_vencimiento: ''
    });
    setItems([]);
    setImagenes([]);
    setModalAbierto(true);
  };

  const abrirModalEdicion = async (cotizacionId) => {
    try {
      const response = await cotizacionService.getById(cotizacionId);
      const cotizacion = response.data;

      // Prevenir edición de cotizaciones aprobadas
      if (cotizacion.estado === 'aprobada') {
        alert('No se puede editar una cotización aprobada');
        return;
      }

      setModoEdicion(true);
      setCotizacionEditando(cotizacion);
      setFormData({
        cliente_id: cotizacion.cliente_id,
        titulo: cotizacion.titulo,
        descripcion: cotizacion.descripcion || '',
        margen_ganancia: cotizacion.margen_ganancia || 0,
        notas: cotizacion.notas || '',
        fecha_vencimiento: cotizacion.fecha_vencimiento ? cotizacion.fecha_vencimiento.split('T')[0] : ''
      });

      // Cargar items de la cotización
      const itemsFormateados = cotizacion.items.map(item => ({
        tipo: item.tipo,
        servicio_id: item.servicio_id || '',
        producto_id: item.producto_id || '',
        descripcion: item.descripcion,
        cantidad: item.cantidad,
        precio_unitario: item.precio_unitario
      }));
      setItems(itemsFormateados);

      // Cargar imágenes de la cotización
      setImagenes(cotizacion.imagenes || []);

      setModalAbierto(true);
    } catch (error) {
      console.error('Error al cargar cotización:', error);
      alert('Error al cargar cotización para editar');
    }
  };

  const abrirModalDetalle = async (cotizacionId) => {
    try {
      const response = await cotizacionService.getById(cotizacionId);
      const cotizacion = response.data;

      setModoEdicion(false);
      setCotizacionEditando(cotizacion);
      setFormData({
        cliente_id: cotizacion.cliente_id,
        titulo: cotizacion.titulo,
        descripcion: cotizacion.descripcion || '',
        margen_ganancia: cotizacion.margen_ganancia || 0,
        notas: cotizacion.notas || '',
        fecha_vencimiento: cotizacion.fecha_vencimiento ? cotizacion.fecha_vencimiento.split('T')[0] : ''
      });

      // Cargar items de la cotización
      const itemsFormateados = cotizacion.items.map(item => ({
        tipo: item.tipo,
        servicio_id: item.servicio_id || '',
        producto_id: item.producto_id || '',
        descripcion: item.descripcion,
        cantidad: item.cantidad,
        precio_unitario: item.precio_unitario
      }));
      setItems(itemsFormateados);

      // Cargar imágenes de la cotización
      setImagenes(cotizacion.imagenes || []);

      setModalAbierto(true);
    } catch (error) {
      console.error('Error al cargar cotización:', error);
      alert('Error al cargar cotización');
    }
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setModoEdicion(false);
    setCotizacionEditando(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    setNuevoItem({
      ...nuevoItem,
      [name]: value
    });

    // Auto-rellenar precio si se selecciona un servicio o producto
    if (name === 'servicio_id' && value) {
      const servicio = servicios.find(s => s.id === parseInt(value));
      if (servicio) {
        setNuevoItem(prev => ({
          ...prev,
          descripcion: servicio.nombre,
          precio_unitario: servicio.precio_base || 0
        }));
      }
    } else if (name === 'producto_id' && value) {
      const producto = productos.find(p => p.id === parseInt(value));
      if (producto) {
        setNuevoItem(prev => ({
          ...prev,
          descripcion: producto.nombre,
          precio_unitario: producto.precio || 0
        }));
      }
    }
  };

  const agregarItem = () => {
    if (!nuevoItem.descripcion || nuevoItem.precio_unitario <= 0) {
      alert('Completa los campos del item');
      return;
    }

    setItems([...items, { ...nuevoItem }]);
    setNuevoItem({
      tipo: 'servicio',
      servicio_id: '',
      producto_id: '',
      descripcion: '',
      cantidad: 1,
      precio_unitario: 0
    });
  };

  const eliminarItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const subirImagen = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten archivos de imagen');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no debe superar los 5MB');
      return;
    }

    try {
      setSubiendoImagen(true);

      const formData = new FormData();
      formData.append('imagen', file);

      const response = await uploadService.subirImagen(formData);
      const nuevaImagen = {
        url: response.data.url,
        descripcion: '',
        esTemporal: true // Marcar como temporal hasta que se guarde la cotización
      };

      // Si estamos en modo edición, guardar la imagen inmediatamente
      if (modoEdicion && cotizacionEditando) {
        const imagenGuardada = await cotizacionService.agregarImagen({
          cotizacion_id: cotizacionEditando.id,
          url: nuevaImagen.url,
          descripcion: nuevaImagen.descripcion
        });
        setImagenes([...imagenes, imagenGuardada.data.imagen]);
      } else {
        // En modo creación, solo agregar a la lista local
        setImagenes([...imagenes, nuevaImagen]);
      }

      // Limpiar input
      e.target.value = '';
    } catch (error) {
      console.error('Error al subir imagen:', error);
      alert('Error al subir la imagen');
    } finally {
      setSubiendoImagen(false);
    }
  };

  const eliminarImagen = async (imagenIndex, imagenId) => {
    try {
      // Si la imagen ya está guardada en la BD, eliminarla
      if (imagenId) {
        await cotizacionService.eliminarImagen(imagenId);
      }
      setImagenes(imagenes.filter((_, i) => i !== imagenIndex));
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      alert('Error al eliminar la imagen');
    }
  };

  const actualizarDescripcionImagen = async (imagenIndex, nuevaDescripcion) => {
    const imagenesActualizadas = [...imagenes];
    imagenesActualizadas[imagenIndex].descripcion = nuevaDescripcion;
    setImagenes(imagenesActualizadas);

    // Si la imagen ya está guardada, actualizar en el backend
    // Por ahora solo actualizamos localmente, se podría agregar un endpoint para actualizar descripción
  };

  const calcularTotal = () => {
    const subtotal = items.reduce((sum, item) => {
      return sum + (item.cantidad * item.precio_unitario);
    }, 0);

    const margen = formData.margen_ganancia ? subtotal * (formData.margen_ganancia / 100) : 0;
    return subtotal + margen;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (items.length === 0) {
      alert('Agrega al menos un item a la cotización');
      return;
    }

    try {
      const datosEnviar = {
        ...formData,
        items: items.map(item => ({
          tipo: item.tipo,
          servicio_id: item.tipo === 'servicio' ? item.servicio_id : null,
          producto_id: item.tipo === 'producto' ? item.producto_id : null,
          descripcion: item.descripcion,
          cantidad: parseFloat(item.cantidad),
          precio_unitario: parseFloat(item.precio_unitario)
        }))
      };

      if (modoEdicion) {
        await cotizacionService.update(cotizacionEditando.id, datosEnviar);
        alert('Cotización actualizada exitosamente');
      } else {
        const response = await cotizacionService.create(datosEnviar);
        const cotizacionCreada = response.data.cotizacion;

        // Si hay imágenes temporales, guardarlas ahora
        if (imagenes.length > 0) {
          for (const imagen of imagenes) {
            if (imagen.esTemporal) {
              await cotizacionService.agregarImagen({
                cotizacion_id: cotizacionCreada.id,
                url: imagen.url,
                descripcion: imagen.descripcion || ''
              });
            }
          }
        }

        alert('Cotización creada exitosamente');
      }

      cargarDatos();
      cerrarModal();
    } catch (error) {
      console.error(`Error al ${modoEdicion ? 'actualizar' : 'crear'} cotización:`, error);
      alert(error.response?.data?.error || `Error al ${modoEdicion ? 'actualizar' : 'crear'} cotización`);
    }
  };

  const enviarCotizacion = async (id) => {
    try {
      await cotizacionService.enviarEmail(id);
      alert('Cotización enviada por email');
      cargarDatos();
    } catch (error) {
      console.error('Error al enviar cotización:', error);
      alert(error.response?.data?.error || 'Error al enviar cotización');
    }
  };

  const cambiarEstado = async (id, nuevoEstado) => {
    try {
      await cotizacionService.cambiarEstado(id, nuevoEstado);
      cargarDatos();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      alert(error.response?.data?.error || 'Error al cambiar estado');
    }
  };

  if (loading) {
    return <div className="loading">Cargando cotizaciones...</div>;
  }

  return (
    <div className="cotizaciones-container">
      <div className="page-header">
        <h1>Cotizaciones</h1>
        <button className="btn btn-primary" onClick={abrirModal}>
          + Nueva Cotización
        </button>
      </div>

      <div className="filtros">
        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="filtro-estado"
        >
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="enviada">Enviada</option>
          <option value="aprobada">Aprobada</option>
          <option value="rechazada">Rechazada</option>
        </select>
      </div>

      {cotizaciones.length === 0 ? (
        <div className="empty-state">
          <p>No hay cotizaciones</p>
          <button className="btn btn-primary" onClick={abrirModal}>
            Crear primera cotización
          </button>
        </div>
      ) : (
        <div className="cotizaciones-list">
          {cotizaciones.map(cotizacion => (
            <div key={cotizacion.id} className="cotizacion-card">
              <div className="cotizacion-header">
                <div>
                  <h3>{cotizacion.titulo}</h3>
                  <p className="cotizacion-numero">{cotizacion.numero_cotizacion}</p>
                </div>
                <span className={`estado-badge ${cotizacion.estado}`}>
                  {cotizacion.estado}
                </span>
              </div>

              <div className="cotizacion-info">
                <p><strong>Cliente:</strong> {cotizacion.cliente_nombre}</p>
                <p><strong>Total:</strong> ${parseFloat(cotizacion.total).toLocaleString()}</p>
                {cotizacion.fecha_vencimiento && (
                  <p><strong>Vence:</strong> {new Date(cotizacion.fecha_vencimiento).toLocaleDateString()}</p>
                )}
              </div>

              <div className="cotizacion-actions">
                {cotizacion.estado === 'aprobada' ? (
                  <>
                    {/* Botones para cotizaciones aprobadas */}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => abrirModalDetalle(cotizacion.id)}
                      title="Ver detalle de la cotización"
                    >
                      👁️ Ver Detalle
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => enviarCotizacion(cotizacion.id)}
                      title="Enviar cotización por email"
                    >
                      📧 Enviar Email
                    </button>
                    <span style={{ color: '#27ae60', fontWeight: 'bold', marginLeft: '10px' }}>
                      ✓ Cotización Aprobada
                    </span>
                  </>
                ) : (
                  <>
                    {/* Botón editar - disponible solo si NO está aprobada */}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => abrirModalEdicion(cotizacion.id)}
                      title="Editar cotización"
                    >
                      ✏️ Editar
                    </button>

                    {/* Botón enviar por email */}
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => enviarCotizacion(cotizacion.id)}
                      title="Enviar cotización por email"
                    >
                      📧 Enviar Email
                    </button>

                    {/* Botones específicos por estado */}
                    {cotizacion.estado === 'borrador' && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => cambiarEstado(cotizacion.id, 'enviada')}
                      >
                        Marcar Enviada
                      </button>
                    )}
                    {cotizacion.estado === 'enviada' && (
                      <>
                        <button
                          className="btn btn-success btn-sm"
                          onClick={() => cambiarEstado(cotizacion.id, 'aprobada')}
                        >
                          ✓ Aprobar
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => cambiarEstado(cotizacion.id, 'rechazada')}
                        >
                          ✕ Rechazar
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalAbierto && (
        <div className="modal-overlay" onClick={cerrarModal}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <h2>
              {cotizacionEditando?.estado === 'aprobada'
                ? 'Detalle de Cotización (Aprobada)'
                : modoEdicion
                ? 'Editar Cotización'
                : 'Nueva Cotización'}
            </h2>

            <form onSubmit={handleSubmit}>
              {cotizacionEditando?.estado === 'aprobada' && (
                <div style={{ padding: '10px', backgroundColor: '#d4edda', borderRadius: '5px', marginBottom: '15px', color: '#155724' }}>
                  ✓ Esta cotización está aprobada y no puede ser modificada
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Cliente *</label>
                  <select
                    name="cliente_id"
                    value={formData.cliente_id}
                    onChange={handleChange}
                    required
                    disabled={cotizacionEditando?.estado === 'aprobada'}
                  >
                    <option value="">Seleccionar cliente</option>
                    {clientes.map(cliente => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Fecha de Vencimiento</label>
                  <input
                    type="date"
                    name="fecha_vencimiento"
                    value={formData.fecha_vencimiento}
                    onChange={handleChange}
                    disabled={cotizacionEditando?.estado === 'aprobada'}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Título *</label>
                <input
                  type="text"
                  name="titulo"
                  value={formData.titulo}
                  onChange={handleChange}
                  required
                  placeholder="Ej: Remodelación de oficina"
                  disabled={cotizacionEditando?.estado === 'aprobada'}
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Descripción general del trabajo"
                  disabled={cotizacionEditando?.estado === 'aprobada'}
                />
              </div>

              <div className="items-section">
                <h3>Items de la Cotización</h3>

                {cotizacionEditando?.estado !== 'aprobada' && (
                  <div className="agregar-item">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Tipo</label>
                      <select
                        name="tipo"
                        value={nuevoItem.tipo}
                        onChange={handleItemChange}
                      >
                        <option value="servicio">Servicio</option>
                        <option value="producto">Producto</option>
                        <option value="mano_obra">Mano de Obra</option>
                      </select>
                    </div>

                    {nuevoItem.tipo === 'servicio' && (
                      <div className="form-group">
                        <label>Servicio</label>
                        <select
                          name="servicio_id"
                          value={nuevoItem.servicio_id}
                          onChange={handleItemChange}
                        >
                          <option value="">Seleccionar o escribir manual</option>
                          {servicios.map(servicio => (
                            <option key={servicio.id} value={servicio.id}>
                              {servicio.nombre} - ${servicio.precio_base}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {nuevoItem.tipo === 'producto' && (
                      <div className="form-group">
                        <label>Producto</label>
                        <select
                          name="producto_id"
                          value={nuevoItem.producto_id}
                          onChange={handleItemChange}
                        >
                          <option value="">Seleccionar o escribir manual</option>
                          {productos.map(producto => (
                            <option key={producto.id} value={producto.id}>
                              {producto.nombre} - ${producto.precio}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Descripción</label>
                    <input
                      type="text"
                      name="descripcion"
                      value={nuevoItem.descripcion}
                      onChange={handleItemChange}
                      placeholder="Descripción del item"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Cantidad</label>
                      <input
                        type="number"
                        name="cantidad"
                        value={nuevoItem.cantidad}
                        onChange={handleItemChange}
                        min="0.01"
                        step="0.01"
                      />
                    </div>

                    <div className="form-group">
                      <label>Precio Unitario</label>
                      <input
                        type="number"
                        name="precio_unitario"
                        value={nuevoItem.precio_unitario}
                        onChange={handleItemChange}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="form-group">
                      <label>Subtotal</label>
                      <input
                        type="text"
                        value={`$${(nuevoItem.cantidad * nuevoItem.precio_unitario).toFixed(2)}`}
                        disabled
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={agregarItem}
                  >
                    + Agregar Item
                  </button>
                </div>
                )}

                {items.length > 0 && (
                  <div className="items-list">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Descripción</th>
                          <th>Cantidad</th>
                          <th>Precio Unit.</th>
                          <th>Subtotal</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.descripcion}</td>
                            <td>{item.cantidad}</td>
                            <td>${parseFloat(item.precio_unitario).toFixed(2)}</td>
                            <td>${(item.cantidad * item.precio_unitario).toFixed(2)}</td>
                            <td>
                              {cotizacionEditando?.estado !== 'aprobada' && (
                                <button
                                  type="button"
                                  className="btn-icon"
                                  onClick={() => eliminarItem(index)}
                                >
                                  🗑️
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Margen de Ganancia (%)</label>
                  <input
                    type="number"
                    name="margen_ganancia"
                    value={formData.margen_ganancia}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    disabled={cotizacionEditando?.estado === 'aprobada'}
                  />
                </div>

                <div className="form-group">
                  <label>Total Estimado</label>
                  <input
                    type="text"
                    value={`$${calcularTotal().toFixed(2)}`}
                    disabled
                    className="total-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Notas</label>
                <textarea
                  name="notas"
                  value={formData.notas}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Notas adicionales"
                  disabled={cotizacionEditando?.estado === 'aprobada'}
                />
              </div>

              {/* Sección de Imágenes */}
              <div className="imagenes-section">
                <h3>Imágenes de la Cotización</h3>
                {cotizacionEditando?.estado !== 'aprobada' && (
                  <div className="upload-area">
                    <input
                      type="file"
                      id="imagen-input"
                      accept="image/*"
                      onChange={subirImagen}
                      style={{ display: 'none' }}
                      disabled={subiendoImagen}
                    />
                    <label htmlFor="imagen-input" className="btn btn-secondary">
                      {subiendoImagen ? 'Subiendo...' : '📷 Agregar Imagen'}
                    </label>
                    <small style={{ marginLeft: '10px', color: '#666' }}>
                      Máximo 5MB por imagen
                    </small>
                  </div>
                )}

                {imagenes.length > 0 && (
                  <div className="imagenes-grid">
                    {imagenes.map((imagen, index) => (
                      <div key={index} className="imagen-item">
                        <img
                          //src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3000'}${imagen.url}`}
			  src={imagen.url.startsWith('http') ? imagen.url : `${window.location.origin}${imagen.url}`}
                          alt={`Imagen ${index + 1}`}
                          className="imagen-preview"
                        />
                        <input
                          type="text"
                          placeholder="Descripción (opcional)"
                          value={imagen.descripcion || ''}
                          onChange={(e) => actualizarDescripcionImagen(index, e.target.value)}
                          className="imagen-descripcion"
                          disabled={cotizacionEditando?.estado === 'aprobada'}
                        />
                        {cotizacionEditando?.estado !== 'aprobada' && (
                          <button
                            type="button"
                            className="btn-eliminar-imagen"
                            onClick={() => eliminarImagen(index, imagen.id)}
                          >
                            🗑️ Eliminar
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-actions">
                {cotizacionEditando?.estado === 'aprobada' ? (
                  <button type="button" className="btn btn-primary" onClick={cerrarModal}>
                    Cerrar
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn btn-secondary" onClick={cerrarModal}>
                      Cancelar
                    </button>
                    <button type="submit" className="btn btn-primary">
                      {modoEdicion ? 'Guardar Cambios' : 'Crear Cotización'}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cotizaciones;
